/**
 * Canonical signup-funnel step definitions for the admin dashboard.
 *
 * Counts always come from `funnel.core_stages` (the milestone rollup) rather than
 * `all_stages`, which undercounts because it tallies raw event types — see
 * docs/ADMIN_FUNNEL_ENDPOINTS.md. `auth_page_viewed` is the exception: it is not
 * in core_stages at all and is sourced from `auth_page_views`.
 */
import type { FunnelChartStep } from '@/components/funnels/FunnelChartViews'
import type { SignupFunnelResponse } from '@/lib/api/admin-types'
import type { FunnelResponse } from '@/lib/types/funnel-api'

type StepDef = {
  event: string
  /** Short label — the column chart truncates long names. */
  label: string
  /** True for branch/exit events that are not sequential conversions. */
  branch?: boolean
}

/** The six-step spine leadership reads: traffic through to a paid trial. */
export const KEY_MILESTONE_STEPS: StepDef[] = [
  { event: 'auth_page_viewed', label: 'Visited auth' },
  { event: 'signup_initiated', label: 'Started signup' },
  { event: 'signup_completed', label: 'Signed up' },
  { event: 'site_added', label: 'Added a site' },
  { event: 'experiment_published', label: 'Launched a test' },
  { event: 'stripe_trial_started', label: 'Started trial' },
]

/**
 * Every tracked step in flow order, including branches and operator signals.
 *
 * Mirrors the wizard, which since 2026-09-10 (v2) puts the URL first and the
 * drafted plan before the tracker: website → you → goal → plan → tracker.
 * Research is no longer a screen: its three events now fire from the plan's
 * in-place strip, so they sit AFTER the goal, not after the site. Reorder this
 * list whenever the wizard reorders, or the funnel reads as a false drop-off.
 */
export const FULL_FUNNEL_STEPS: StepDef[] = [
  { event: 'signup_initiated', label: 'Started signup' },
  { event: 'verification_failed', label: 'Verify failed', branch: true },
  { event: 'signup_completed', label: 'Signed up' },
  { event: 'onboarding_started', label: 'Onboarding' },
  { event: 'site_added', label: 'Added a site' },
  { event: 'site_added_duplicate', label: 'Duplicate site', branch: true },
  { event: 'onboarding_icp_selected', label: 'ICP picked' },
  { event: 'onboarding_goal_selected', label: 'Goal picked' },
  { event: 'onboarding_express_skipped', label: 'Express skip', branch: true },
  { event: 'onboarding_research_started', label: 'Research started' },
  { event: 'onboarding_research_completed', label: 'Research done' },
  { event: 'onboarding_research_failed', label: 'Research failed', branch: true },
  { event: 'onboarding_plan_viewed', label: 'Plan viewed' },
  { event: 'onboarding_plan_cta_clicked', label: 'Plan CTA' },
  { event: 'onboarding_call_shown', label: 'Call shown' },
  { event: 'onboarding_call_clicked', label: 'Call booked', branch: true },
  { event: 'onboarding_call_dismissed', label: 'Call dismissed', branch: true },
  { event: 'onboarding_script_copied', label: 'Script copied' },
  { event: 'onboarding_install_instructions_expanded', label: 'Install help' },
  { event: 'onboarding_dev_email_sent', label: 'Dev emailed' },
  { event: 'onboarding_verification_skipped', label: 'Verify skipped', branch: true },
  { event: 'onboarding_completed', label: 'Onboarded' },
  { event: 'api_key_generated', label: 'API key' },
  { event: 'mcp_prompt_copied', label: 'MCP prompt' },
  { event: 'experiment_published', label: 'Launched a test' },
  { event: 'stripe_trial_started', label: 'Started trial' },
]

/**
 * The canonical event chain as operators read it, for the reference tooltip on the
 * funnel header. Inner arrays are alternatives at the same position in the flow
 * (a user picks a goal *or* skips onboarding via Express), rendered "a / b".
 */
export const SIGNUP_EVENT_SEQUENCE: string[][] = [
  ['signup_initiated'],
  ['verification_failed'],
  ['signup_completed'],
  ['onboarding_started'],
  ['site_added', 'site_added_duplicate'],
  ['onboarding_icp_selected'],
  ['onboarding_goal_selected', 'onboarding_express_skipped'],
  ['onboarding_research_started'],
  ['onboarding_research_completed', 'onboarding_research_failed'],
  ['onboarding_plan_viewed'],
  ['onboarding_plan_cta_clicked'],
  ['onboarding_call_shown'],
  ['onboarding_call_clicked', 'onboarding_call_dismissed'],
  ['onboarding_script_copied'],
  ['onboarding_install_instructions_expanded'],
  ['onboarding_dev_email_sent'],
  ['onboarding_verification_skipped'],
  ['onboarding_completed'],
  ['experiment_published'],
  ['stripe_trial_started'],
]

/** Events that are exits/branches rather than forward conversions. */
export const BRANCH_EVENTS = new Set(
  FULL_FUNNEL_STEPS.filter((s) => s.branch).map((s) => s.event)
)

/**
 * Resolve step counts into FunnelChartStep rows. Steps the API returned no data
 * for render as 0 rather than disappearing, so a missing stage stays visible.
 */
export function buildFunnelSteps(
  data: SignupFunnelResponse,
  defs: StepDef[]
): FunnelChartStep[] {
  const counts = new Map<string, number>()
  for (const stage of data.funnel?.core_stages ?? []) {
    counts.set(stage.event_type, stage.count || stage.unique_sessions || 0)
  }
  if (data.auth_page_views) {
    counts.set('auth_page_viewed', data.auth_page_views.unique_sessions)
  }

  return defs.map((def) => {
    const value = counts.get(def.event) ?? 0
    return {
      id: def.event,
      name: def.label,
      inFunnel: value,
      matches: value,
      targetLabel: def.branch ? `${def.event} · exit` : def.event,
      conditionCount: 1,
      crosses: false,
      tone: def.branch ? ('caution' as const) : ('default' as const),
    }
  })
}

/**
 * Adapt the admin steps into the property-scoped `FunnelResponse` the shared flow
 * (Nivo) chart consumes. The signup funnel is account-wide rather than
 * property-scoped, so `meta` carries placeholders the chart never reads.
 */
export function buildFunnelResponse(
  steps: FunnelChartStep[],
  range: { from: Date; to: Date }
): FunnelResponse {
  const entered = steps[0]?.inFunnel ?? 0
  const completed = steps.at(-1)?.inFunnel ?? 0

  return {
    meta: {
      property_id: 'admin',
      start: range.from.toISOString(),
      end: range.to.toISOString(),
      timezone: 'UTC',
      mode: 'sequential',
      step_count: steps.length,
    },
    data: {
      steps: steps.map((step, i) => {
        const previous = i > 0 ? steps[i - 1].inFunnel : step.inFunnel
        const dropped = Math.max(previous - step.inFunnel, 0)
        return {
          step_index: i,
          step_name: step.name,
          total_sessions: step.inFunnel,
          total_events: step.totalEvents ?? step.inFunnel,
          drop_off_count: dropped,
          drop_off_rate: previous > 0 ? dropped / previous : 0,
          conversion_rate: entered > 0 ? step.inFunnel / entered : 0,
          avg_time_from_previous: null,
          median_time_from_previous: null,
        }
      }),
    },
    summary: {
      total_entered: entered,
      total_completed: completed,
      overall_conversion_rate: entered > 0 ? completed / entered : 0,
      avg_completion_time: null,
    },
  }
}
